import React from "react";
import { Formik, Field, FieldProps } from "formik";
import { Checkbox } from "antd";
import * as S from "../styles";
import { InputWrapper } from "../../../components/InputWrapper";
import { DSInput } from "../../../components/DSInput";
import { formatDate } from "../../../utils/formatDate";
import {
  getInstallmentCalculatorSchema,
  InstallmentCalculatorSchema,
} from "./schema";
import Can from "../../../components/Can";
import {
  formatNumberToPtBr,
  formatPercent,
  formatTaxValue,
  onChangeInitialValue,
  parseValueToNumber,
} from "../utils";
import { InstallmentCalculatorProps } from "./types";
import { hasProcessorPermission } from "../../../helpers/hasProcessorPermission";
import { PermissionManagement } from "../../../pages/PermissionsManagement/types";

export const InstallmentCalculator = ({
  parameters,
  selectedInstallmentOption,
  installmentOptions,
  hasPermissionToChangeTax,
  isLoading,
  onSimulate,
}: InstallmentCalculatorProps) => {
  const savedData = sessionStorage.getItem("@SAC_RPE_PERMISSION_MANAGEMENT");

  const permissionManagement: PermissionManagement = savedData
    ? JSON.parse(savedData)
    : { currentProcessor: undefined, apiData: [] };

  const installmentFields = [
    {
      shouldShow: !!selectedInstallmentOption?.quantidadeParcelas,
      name: "taxaIOF",
      label: "Taxa de IOF diário (%a.d.)",
      "data-testid": "input_taxa_iof_parcelamento_fatura",
      formatValue: () =>
        formatTaxValue({
          taxaIof: selectedInstallmentOption?.taxaIof ?? 0,
        }),
    },
    {
      shouldShow: !!selectedInstallmentOption?.quantidadeParcelas,
      name: "taxaIOFAdicional",
      label: "Taxa de IOF adicional (%a.m.)",
      "data-testid": "input_taxa_iof_adicional_parcelamento_fatura",
      formatValue: () =>
        formatTaxValue({
          taxaIof: selectedInstallmentOption?.taxaIofAdicional ?? 0,
        }),
    },
    {
      shouldShow: !!installmentOptions?.[0]?.dataRecomendavelAtivacao,
      name: "recomendavelAtivacao",
      label: "Recomendável ativação até:",
      "data-testid": "input_recomendavel_ativacao_parcelamento_fatura",
      formatValue: () =>
        formatDate(installmentOptions?.[0]?.dataRecomendavelAtivacao),
      type: "datetime",
    },
  ];

  const minTaxValue = parameters?.valorMinimo ?? 0;
  const maxTaxValue = parameters?.valorPadrao ?? minTaxValue;

  const installmentCalculatorSchema = getInstallmentCalculatorSchema(
    minTaxValue,
    maxTaxValue
  );

  return (
    <S.CommonSection>
      <S.SectionTitle>Calcular Parcelamento</S.SectionTitle>
      <Formik<InstallmentCalculatorSchema>
        initialValues={{
          tax: selectedInstallmentOption?.tax || maxTaxValue,
          customInitialValue: !!selectedInstallmentOption?.initialValue,
          initialValue: selectedInstallmentOption?.initialValue
            ? formatNumberToPtBr(selectedInstallmentOption.initialValue)
            : undefined,
        }}
        validationSchema={installmentCalculatorSchema}
        onSubmit={(values) =>
          onSimulate({
            tax: Number(values.tax),
            initialValue: values.initialValue
              ? parseValueToNumber(values.initialValue)
              : undefined,
          })
        }
      >
        {({ isSubmitting, values, setFieldValue, setFieldError }) => (
          <S.InstallmentCalculator.CustomForm autoComplete="off">
            <S.InstallmentCalculator.FormWrapper>
              <Field name="tax">
                {({ field, meta }: FieldProps<number>) => {
                  return (
                    <InputWrapper
                      label="Taxa de refinanciamento (%a.m.)"
                      name={field.name}
                      isRequired
                      error={
                        meta.touched && meta.error ? meta.error : undefined
                      }
                    >
                      <DSInput
                        {...field}
                        type="number"
                        disabled={
                          !hasPermissionToChangeTax || isLoading || isSubmitting
                        }
                        touched={meta.touched}
                        error={!!meta.error}
                        data-testid="input_taxa_parcelamento_fatura"
                        min={minTaxValue}
                        max={maxTaxValue}
                        step="any"
                        lang="pt-BR"
                      />
                      <S.InstallmentCalculator.RangeTaxInput>
                        Min: {formatPercent(minTaxValue, 3)}% Max:{" "}
                        {formatPercent(maxTaxValue, 3)}%
                      </S.InstallmentCalculator.RangeTaxInput>
                    </InputWrapper>
                  );
                }}
              </Field>

              {hasProcessorPermission(
                "installment",
                permissionManagement?.currentProcessor as "cards" | "dock"
              ) && (
                <Can permission="use_custom_initial_value_in_installment">
                  <S.InstallmentCalculator.CheckboxWrapper>
                    <Field name="customInitialValue">
                      {({ field }: FieldProps<boolean>) => {
                        return (
                          <Checkbox
                            {...field}
                            id={field.name}
                            checked={field.value}
                            disabled={isLoading || isSubmitting}
                            data-testid="checkbox_entrada_personalizada_parcelamento_fatura"
                            onChange={(e) => {
                              const {
                                target: { checked },
                              } = e;
                              field.onChange(e);
                              if (checked) {
                                setFieldValue("initialValue", "R$ 0,00");
                              } else {
                                setFieldValue("initialValue", undefined);
                                setFieldError("initialValue", undefined);
                              }
                            }}
                          >
                            Entrada personalizada
                          </Checkbox>
                        );
                      }}
                    </Field>
                  </S.InstallmentCalculator.CheckboxWrapper>
                  {values.customInitialValue && (
                    <Field name="initialValue">
                      {({ field, meta }: FieldProps<string>) => {
                        return (
                          <InputWrapper
                            label="Entrada personalizada"
                            name={field.name}
                            isRequired
                            error={
                              meta.touched && meta.error
                                ? meta.error
                                : undefined
                            }
                          >
                            <DSInput
                              {...field}
                              inputMode="numeric"
                              disabled={isSubmitting}
                              touched={meta.touched}
                              error={!!meta.error}
                              data-testid="input_entrada_personalizada_parcelamento_fatura"
                              onChange={(event) =>
                                onChangeInitialValue(
                                  event.target.value,
                                  setFieldValue
                                )
                              }
                            />
                          </InputWrapper>
                        );
                      }}
                    </Field>
                  )}
                </Can>
              )}

              <S.InstallmentCalculator.SimulateButton
                buttonType="secondary"
                type="submit"
                buttonText="Simular"
                height={30}
                style={{
                  padding: "0.4rem 1.5rem",
                }}
                disabled={isLoading || isSubmitting}
                isDisabled={isLoading || isSubmitting}
                isLoading={isLoading || isSubmitting}
                dataTestId="botao_simular_parcelamento_fatura"
              />
            </S.InstallmentCalculator.FormWrapper>
            <S.InstallmentCalculator.FieldsWrapper>
              {installmentFields.map((field) => (
                <Field
                  name={field.name}
                  key={`installment-calculator-${field.name}`}
                >
                  {({ field: internalField }: FieldProps<string | number>) => {
                    const { shouldShow, formatValue, ...restField } = field;
                    const value = formatValue
                      ? formatValue()
                      : internalField.value;

                    return shouldShow ? (
                      <InputWrapper label={field.label} name={field.name}>
                        <DSInput
                          {...internalField}
                          {...restField}
                          disabled
                          value={value}
                        />
                      </InputWrapper>
                    ) : null;
                  }}
                </Field>
              ))}
            </S.InstallmentCalculator.FieldsWrapper>
          </S.InstallmentCalculator.CustomForm>
        )}
      </Formik>
    </S.CommonSection>
  );
};
